import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Button } from '../ui/button'
import { Logo } from '../ui/Logo'
import { Avatar, AvatarFallback } from '../ui/avatar'
import { Menu, X, Globe } from 'lucide-react'
import { useState, useEffect } from 'react'
import type { User } from '../../types'
import { useI18n } from '../../i18n'

interface NavbarProps {
  isScrolled: boolean
  isLoggedIn: boolean
  isAdmin: boolean
  user: User | null
  onLogout: () => void
}

export function Navbar({ isScrolled, isLoggedIn, isAdmin, user, onLogout }: NavbarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('')
  const { lang, toggleLang, t } = useI18n()

  useEffect(() => {
    const sections = ['capabilities', 'how-it-works', 'use-cases']
    const handleScroll = () => {
      for (const id of sections.reverse()) {
        const el = document.getElementById(id)
        if (el && el.getBoundingClientRect().top <= 200) {
          setActiveSection(id)
          return
        }
      }
      setActiveSection('')
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { href: '#capabilities', label: t('产品能力', 'Product'), id: 'capabilities' },
    { href: '#how-it-works', label: t('解决方案', 'Solutions'), id: 'how-it-works' },
    { href: '#use-cases', label: t('行业案例', 'Cases'), id: 'use-cases' },
    { href: '/pricing', label: t('定价', 'Pricing'), id: 'pricing' },
    { href: '/about', label: t('关于公司', 'Company'), id: 'about' },
  ]

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/70 backdrop-blur-2xl border-b border-black/[0.04] shadow-[0_1px_0_rgba(0,0,0,0.03)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-14">
            <Link to="/" className="flex items-center gap-2 text-lg font-semibold mr-8 shrink-0">
              <Logo size={26} />
              <span className="text-[#1a1a1a]">PrimeBook</span>
              <span className="text-orange-500">AI</span>
            </Link>

            <nav className="hidden md:flex items-center gap-1 mr-auto">
              {navLinks.map((link) => (
                link.href.startsWith('#') ? (
                  <a
                    key={link.id}
                    href={link.href}
                    className={`relative px-3 py-1.5 text-[13px] rounded-lg transition-all duration-300 ${
                      activeSection === link.id
                        ? 'text-[#1a1a1a] font-medium bg-black/[0.04]'
                        : isScrolled
                        ? 'text-gray-400 hover:text-[#1a1a1a]'
                        : 'text-gray-500 hover:text-[#1a1a1a]'
                    }`}
                  >
                    {link.label}
                    {activeSection === link.id && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-orange-500 rounded-full" />
                    )}
                  </a>
                ) : (
                  <Link
                    key={link.id}
                    to={link.href}
                    className={`px-3 py-1.5 text-[13px] rounded-lg transition-all duration-300 ${
                      location.pathname === link.href
                        ? 'text-[#1a1a1a] font-medium bg-black/[0.04]'
                        : isScrolled
                        ? 'text-gray-400 hover:text-[#1a1a1a]'
                        : 'text-gray-500 hover:text-[#1a1a1a]'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-2 shrink-0">
            {/* Language toggle */}
            <button
              onClick={toggleLang}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[12px] rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all duration-200"
            >
              <Globe className="h-3.5 w-3.5" />
              {lang === 'zh' ? 'EN' : '中'}
            </button>

            {isLoggedIn && user ? (
              <div className="flex items-center gap-2">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-orange-500 text-white text-[11px]">
                    {user.nickName?.[0] || user.username?.[0] || 'U'}
                  </AvatarFallback>
                </Avatar>
                {isAdmin && (
                  <Button
                    size="sm"
                    className="bg-orange-500 text-white hover:bg-orange-600 text-[13px] h-8 px-3 rounded-lg font-medium"
                    onClick={() => navigate('/admin')}
                  >
                    {t('管理后台', 'Admin')}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onLogout}
                  className="text-gray-400 hover:text-[#1a1a1a] text-[13px] h-8 px-2"
                >
                  {t('退出', 'Logout')}
                </Button>
                <Button
                  size="sm"
                  className="bg-[#1a1a1a] text-white hover:bg-[#333] text-[13px] h-8 px-3 rounded-lg font-medium"
                  onClick={() => navigate('/workspace')}
                >
                  {t('工作台', 'Dashboard')}
                </Button>
              </div>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-[#1a1a1a] text-[13px] h-8 px-3"
                  onClick={() => navigate('/login')}
                >
                  {t('登录', 'Login')}
                </Button>
                <Button
                  size="sm"
                  className="bg-[#1a1a1a] text-white hover:bg-[#333] text-[13px] h-8 px-3 rounded-lg font-medium"
                  onClick={() => navigate('/login?mode=register')}
                >
                  {t('免费试用', 'Free Trial')}
                </Button>
              </>
            )}
          </div>

          <div className="md:hidden flex items-center gap-2 ml-auto">
            <button
              onClick={toggleLang}
              className="flex items-center gap-1 px-2 py-1.5 text-[12px] rounded-lg text-gray-400"
            >
              <Globe className="h-3.5 w-3.5" />
              {lang === 'zh' ? 'EN' : '中'}
            </button>
            <button
              className="p-2 text-gray-400 hover:text-[#1a1a1a]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white/90 backdrop-blur-2xl border-b border-black/[0.04]">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              link.href.startsWith('#') ? (
                <a
                  key={link.id}
                  href={link.href}
                  className={`block text-sm px-3 py-2 rounded-lg transition-colors ${
                    activeSection === link.id
                      ? 'text-[#1a1a1a] font-medium bg-black/[0.04]'
                      : 'text-gray-400'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.id}
                  to={link.href}
                  className={`block text-sm px-3 py-2 rounded-lg transition-colors ${
                    location.pathname === link.href
                      ? 'text-[#1a1a1a] font-medium bg-black/[0.04]'
                      : 'text-gray-400'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              )
            ))}
            <div className="pt-3 mt-2 border-t border-black/[0.04] flex gap-2">
              {isLoggedIn ? (
                <>
                  {isAdmin && (
                    <Button size="sm" className="bg-orange-500 text-white rounded-lg font-medium" onClick={() => { navigate('/admin'); setMobileMenuOpen(false) }}>
                      {t('管理后台', 'Admin')}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="text-gray-400" onClick={() => { onLogout(); setMobileMenuOpen(false) }}>
                    {t('退出', 'Logout')}
                  </Button>
                  <Button size="sm" className="bg-[#1a1a1a] text-white rounded-lg font-medium" onClick={() => { navigate('/workspace'); setMobileMenuOpen(false) }}>
                    {t('工作台', 'Dashboard')}
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="ghost" className="text-gray-400" onClick={() => { navigate('/login'); setMobileMenuOpen(false) }}>
                    {t('登录', 'Login')}
                  </Button>
                  <Button size="sm" className="bg-[#1a1a1a] text-white rounded-lg font-medium" onClick={() => { navigate('/login?mode=register'); setMobileMenuOpen(false) }}>
                    {t('免费试用', 'Free Trial')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
